import React, { ReactNode, useContext, useEffect, useState } from 'react';

import { get, post } from '~/utils/request';
import storage from '~/utils/storage';

export interface IAuthUser {
  id: string;
  name: string;
  account?: string;
  email: string;
  avatar?: string;
}

export interface IAuthTokenData {
  code: string;
  redirect_uri: string;
  client_id: string;
}

export interface UserLoginRequest {
  /**
   * User's email address
   */
  email: string;
  /**
   * User's password
   */
  password: string;
}

export interface LoginResponse {
  /**
   * User data returned after successful login
   */
  user: IAuthUser;
  /**
   * Success message from the server
   */
  message: string;
}

interface IAuthContext {
  user: IAuthUser | null;
  isLoadingUser: boolean;
  isLoadingEmail: boolean;
  isLoadingGoogle: boolean;
  isAuthenticated: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<boolean>;
  handleAuthCallback: (code: string, redirectUri: string) => Promise<void>;
}

// API base URL is now managed in the request utility

const AuthContext = React.createContext<IAuthContext>({
  user: null,
  isLoadingUser: false,
  isLoadingEmail: false,
  isLoadingGoogle: false,
  isAuthenticated: false,
  signInWithGoogle: () => Promise.resolve(),
  signInWithEmail: () => Promise.resolve(),
  signOut: () => Promise.resolve(false),
  handleAuthCallback: () => Promise.resolve(),
});

export const AuthContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<IAuthUser | null>(null);
  const [isLoadingGoogle, setLoadingGoogle] = useState(false);
  const [isLoadingEmail, setLoadingEmail] = useState(false);
  const [isLoadingUser, setLoadingUser] = useState(false);

  useEffect(() => {
    const checkUserLoggedIn = async () => {
      setLoadingUser(true);
      try {
        const storedUser = await storage.get<IAuthUser>('user');

        if (storedUser) {
          setUser(storedUser);
        } else {
          try {
            const userData = await get<IAuthUser>({
              url: '/api/v1/auth/user',
            });

            if (userData) {
              setUser(userData);
              await storage.set('user', userData);
            }
          } catch {
            // Just set user to null and don't log the error
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Error checking user login status', error);
        setUser(null);
      } finally {
        setLoadingUser(false);
      }
    };

    checkUserLoggedIn();
  }, []);

  const signInWithGoogle = async () => {
    try {
      setLoadingGoogle(true);

      const { client_id, redirect_uri } = await get<IAuthTokenData>({
        url: '/api/v1/auth/google/url',
        withCredentials: false, // Don't send credentials for this public endpoint
      });

      // Store the redirect URI in session storage for later use
      sessionStorage.setItem('oauth_redirect_uri', redirect_uri);

      // Store the current path to return to after authentication
      // We'll use the current URL path if available
      const currentPath = window.location.pathname;
      if (currentPath !== '/auth' && currentPath !== '/auth/callback') {
        sessionStorage.setItem('auth_redirect_path', currentPath + window.location.search);
      } else {
        // If we're already on the auth page, check if we have a stored path to return to
        const storedPath = sessionStorage.getItem('auth_redirect_path');
        if (!storedPath) {
          // If not, default to home
          sessionStorage.setItem('auth_redirect_path', '/');
        }
      }

      // Construct the Google OAuth URL
      const googleAuthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');

      googleAuthUrl.searchParams.append('client_id', client_id);
      googleAuthUrl.searchParams.append('redirect_uri', redirect_uri);
      googleAuthUrl.searchParams.append('response_type', 'code');
      googleAuthUrl.searchParams.append('scope', 'openid email profile');
      googleAuthUrl.searchParams.append('prompt', 'select_account');

      // Redirect to Google OAuth page in the same window
      window.location.href = googleAuthUrl.toString();
    } catch (error) {
      console.error('Error signing in with Google', error);
      setLoadingGoogle(false);
    }
  };

  const handleAuthCallback = async (code: string, redirect_uri: string) => {
    try {
      setLoadingUser(true);
      // Exchange the authorization code for tokens and user data
      // The token will be set as an HttpOnly cookie by the server
      const userData = await post<{ user: IAuthUser }>({
        url: '/api/v1/auth/google/token',
        data: {
          code,
          redirect_uri,
        },
        withCredentials: true, // Include credentials to store the HttpOnly cookie
      });

      // Save the user to state and storage
      setUser(userData.user);
      await storage.set('user', userData.user);

      // Redirect to the stored path after successful authentication
      window.location.href = sessionStorage.getItem('auth_redirect_path') || '/';
    } catch (error) {
      console.error('Error exchanging code for tokens', error);
    } finally {
      setLoadingUser(false);
    }
  };

  const signOut = async () => {
    try {
      setLoadingUser(true);

      // Call the logout endpoint to clear the HttpOnly cookie
      await get({ url: '/api/v1/auth/logout' });

      // Clear user data from state and storage
      setUser(null);
      await storage.remove('user');

      return true;
    } catch (error) {
      console.error('Error signing out', error);
      return false;
    } finally {
      setLoadingUser(false);
    }
  };

  const signInWithEmail = async (email: string, password: string): Promise<void> => {
    try {
      setLoadingEmail(true);

      const currentPath = window.location.pathname;
      if (currentPath === '/auth' || currentPath === '/auth/callback') {
        const storedPath = sessionStorage.getItem('auth_redirect_path');
        if (!storedPath) {
          sessionStorage.setItem('auth_redirect_path', '/');
        }
      }

      const userData = await post<LoginResponse>({
        url: '/api/v1/auth/login',
        data: {
          email,
          password,
        },
        withCredentials: true, // Include credentials to store the HttpOnly cookie
      });

      // Save the user to state and storage
      setUser(userData.user);
      await storage.set('user', userData.user);

      // Redirect to the stored path after successful authentication
      window.location.href = sessionStorage.getItem('auth_redirect_path') || '/';
    } catch (error) {
      console.error('Error during email/password login', error);
      throw error;
    } finally {
      setLoadingEmail(false);
    }
  };

  const authContextValue = {
    user,
    isLoadingUser,
    isLoadingGoogle,
    isLoadingEmail,
    signInWithGoogle,
    signInWithEmail,
    signOut,
    isAuthenticated: !!user,
    handleAuthCallback,
  };

  return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): IAuthContext => {
  return useContext(AuthContext);
};
